import React from 'react'
import { FormValueObject } from '#src/components/Path/Form'
import { FormContextConsumer, FormContextType } from '#src/components/Path/FormContext'
import {
  DateRangePathFlowQuestionFragment,
  DatePathFlowQuestionFragment,
  PathFlowQuestionFragment,
  ListPathFlowQuestionFragment,
  InputTextPathFlowQuestionFragment,
  NumericPathFlowQuestionFragment,
  OAuthEmailPathFlowQuestionFragment,
  SharedPathFlowQuestionFragment,
  IdPathFlowQuestionFragment
} from '#src/generated/graphql'

interface ContentfulSettings {
  content?:
    | {
        [key: string]: any
      }
    | null
    | undefined
}

export type ListPathFlowQuestionDefinition<T = {}> = PathQuestionProps<
  ListPathFlowQuestionFragment
> &
  T

export type InputTextPathFlowQuestionDefintion<T = {}> = PathQuestionProps<
  InputTextPathFlowQuestionFragment
> &
  T

export type IdPathFlowQuestionDefinition<T = {}> = PathQuestionProps<IdPathFlowQuestionFragment> & T

export type NumericPathFlowQuestionDefinition<T = {}> = PathQuestionProps<
  NumericPathFlowQuestionFragment
> &
  T

export type OAuthEmailPathFlowQuestionDefinition<T = {}> = PathQuestionProps<
  OAuthEmailPathFlowQuestionFragment
> &
  T

export type DateRangePathFlowQuestionDefinition<T = {}> = PathQuestionProps<
  DateRangePathFlowQuestionFragment
> &
  T

export type DatePathFlowQuestionDefinition<T = {}> = PathQuestionProps<
  DatePathFlowQuestionFragment
> &
  T

export interface QuestionComponentMap {
  InputTextPathFlowQuestion: React.ComponentType<InputTextPathFlowQuestionDefintion<{}>>
  IdPathFlowQuestion: React.ComponentType<IdPathFlowQuestionDefinition<{}>>
  ListPathFlowQuestion: React.ComponentType<ListPathFlowQuestionDefinition<{}>>
  NumericPathFlowQuestion: React.ComponentType<NumericPathFlowQuestionDefinition<{}>>
  OAuthEmailPathFlowQuestion: React.ComponentType<OAuthEmailPathFlowQuestionDefinition<{}>>
  DateRangePathFlowQuestion: React.ComponentType<DateRangePathFlowQuestionDefinition<{}>>
  DatePathFlowQuestion: React.ComponentType<DatePathFlowQuestionDefinition<{}>>
}

export interface PathQuestionProps<T> {
  question: T & SharedPathFlowQuestionFragment
  onFieldValidityChanged: (valid: boolean) => void
  onFieldValueChanged: (value: FormValueObject, callback?: (() => void) | undefined) => void
  contentfulSettings?: ContentfulSettings
}

interface QuestionProps {
  question: PathFlowQuestionFragment
  questionIndex: number
  questionComponentMap: QuestionComponentMap
  contentfulSettings?: ContentfulSettings
}

class Question extends React.Component<QuestionProps & FormContextType> {
  public render() {
    const { question, contentfulSettings } = this.props

    switch (question.__typename) {
      case 'ListPathFlowQuestion':
        const ListPathFlowQuestionComponent = this.props.questionComponentMap.ListPathFlowQuestion

        return (
          <ListPathFlowQuestionComponent
            key={question.internalName}
            question={question}
            onFieldValidityChanged={valid =>
              this.props.onFieldValidityChanged(question.internalName, valid)
            }
            onFieldValueChanged={(value, callback) => {
              this.props.onFieldValueChanged(question.internalName, value, callback)
            }}
            contentfulSettings={contentfulSettings}
          />
        )

      case 'InputTextPathFlowQuestion':
        const InputTextPathFlowQuestionComponent = this.props.questionComponentMap
          .InputTextPathFlowQuestion

        return (
          <InputTextPathFlowQuestionComponent
            key={question.internalName}
            question={question}
            onFieldValidityChanged={valid =>
              this.props.onFieldValidityChanged(question.internalName, valid)
            }
            onFieldValueChanged={(value, callback) => {
              this.props.onFieldValueChanged(question.internalName, value, callback)
            }}
          />
        )

      case 'NumericPathFlowQuestion':
        const NumericPathFlowQuestionComponent = this.props.questionComponentMap
          .NumericPathFlowQuestion

        return (
          <NumericPathFlowQuestionComponent
            key={question.internalName}
            question={question}
            onFieldValidityChanged={valid =>
              this.props.onFieldValidityChanged(question.internalName, valid)
            }
            onFieldValueChanged={(value, callback) => {
              this.props.onFieldValueChanged(question.internalName, value, callback)
            }}
          />
        )

      case 'OAuthEmailPathFlowQuestion':
        const OAuthEmailPathFlowQuestionComponent = this.props.questionComponentMap
          .OAuthEmailPathFlowQuestion

        return (
          <OAuthEmailPathFlowQuestionComponent
            key={question.internalName}
            question={question}
            onFieldValidityChanged={valid =>
              this.props.onFieldValidityChanged(question.internalName, valid)
            }
            onFieldValueChanged={(value, callback) => {
              this.props.onFieldValueChanged(question.internalName, value, callback)
            }}
          />
        )

      case 'IdPathFlowQuestion':
        const IdPathFlowQuestionComponent = this.props.questionComponentMap.IdPathFlowQuestion

        return (
          <IdPathFlowQuestionComponent
            key={question.internalName}
            question={question}
            onFieldValidityChanged={valid =>
              this.props.onFieldValidityChanged(question.internalName, valid)
            }
            onFieldValueChanged={(value, callback) => {
              this.props.onFieldValueChanged(question.internalName, value, callback)
            }}
          />
        )

      case 'DateRangePathFlowQuestion':
        const DateRangePathFlowQuestionComponent = this.props.questionComponentMap
          .DateRangePathFlowQuestion

        return (
          <DateRangePathFlowQuestionComponent
            key={question.internalName}
            question={question}
            onFieldValidityChanged={valid =>
              this.props.onFieldValidityChanged(question.internalName, valid)
            }
            onFieldValueChanged={(value, callback) => {
              this.props.onFieldValueChanged(question.internalName, value, callback)
            }}
          />
        )

      case 'DatePathFlowQuestion':
        const DatePathFlowQuestionComponent = this.props.questionComponentMap.DatePathFlowQuestion

        return (
          <DatePathFlowQuestionComponent
            key={question.internalName}
            question={question}
            onFieldValidityChanged={valid =>
              this.props.onFieldValidityChanged(question.internalName, valid)
            }
            onFieldValueChanged={(value, callback) => {
              this.props.onFieldValueChanged(question.internalName, value, callback)
            }}
          />
        )

      default:
        throw new Error(`Unexpected question type passed`)
    }
  }
}

const QuestionWithContext: React.SFC<QuestionProps> = ({ ...rest }) => (
  <FormContextConsumer>
    {formData => (formData ? <Question {...formData} {...rest} /> : null)}
  </FormContextConsumer>
)

export default QuestionWithContext
